import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { CalendarDays } from 'lucide-react';
import { api } from '../api/client';
import {
  formatCalendarDate,
  parseCalendarDate,
  tenantCalendarDate,
  toCalendarDateString,
} from '../lib/calendar-date';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../lib/load-failure';
import { LoadFailureNotice } from './ui/LoadFailureNotice';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';
import { overlapName } from './TimeOffOverlaps';
import type { TimeOffOverlap } from '../types/time-off-overlap';

/**
 * Who on the crew is already booked off in the next 60 days, shown on the
 * time-off screen BEFORE the hand has picked any dates.
 *
 * TNT asked for "a calendar on the field techs' days off" so a hand booking
 * time can see who already has it. A month view was rejected on Sep 14: too
 * small on a phone, and the wrong shape for a 40-hand crew. This is a list,
 * grouped by week, in a scroll box. Once both dates are picked,
 * `TimeOffOverlaps` answers the narrower question for those dates and this list
 * steps aside.
 *
 * SAME ENDPOINT, SAME FIVE COLUMNS. `GET /employee/time-off/overlaps` with no
 * window is the next 60 days from the TENANT's today (api#843). The API works
 * out that today, from companies.time_zone. It is approved-only and leaves the
 * caller out. No type, no reason, no status: that crew members are off is the
 * crew's business, why they are off is not.
 *
 * GROUPED BY THE WEEK THE ABSENCE IS ON THE CALENDAR, Monday first. Every
 * value here is a calendar day ('YYYY-MM-DD'), so grouping is calendar
 * arithmetic on local construction. No instant is parsed and the device zone
 * never enters into it. An absence that began before today is filed under THIS
 * week, not the week it started. Leave that began in August still covers today,
 * and "Week of Jul 27" at the top of an upcoming list would read as stale data.
 * "Today" for that clamp is the tenant's day. Until the locale loads it is
 * unknown, and the clamp then simply does not happen (rows group by their own
 * start). The API has already bounded the window either way.
 */

const DAY = (value: string) =>
  formatCalendarDate(value, { month: 'short', day: 'numeric' }) ?? '—';

/** Monday of the week `day` falls in, as 'YYYY-MM-DD'. Null for no readable day. */
export function weekOf(day: string | null | undefined): string | null {
  const d = parseCalendarDate(day);
  if (!d) return null;
  const back = (d.getDay() + 6) % 7; // Mon → 0 … Sun → 6
  return toCalendarDateString(new Date(d.getFullYear(), d.getMonth(), d.getDate() - back));
}

export interface WeekGroup {
  week: string;
  rows: TimeOffOverlap[];
}

/**
 * Rows into Monday-weeks, in date order. `today` is the tenant's day or null.
 * A row that started before today groups under today's week.
 */
export function groupByWeek(rows: TimeOffOverlap[], today: string | null): WeekGroup[] {
  const groups = new Map<string, TimeOffOverlap[]>();
  const sorted = [...rows].sort((a, b) =>
    a.start_date === b.start_date ? overlapName(a).localeCompare(overlapName(b))
      : a.start_date < b.start_date ? -1 : 1);
  for (const row of sorted) {
    // Both sides are 'YYYY-MM-DD', so the string compare IS the calendar one.
    const anchor = today && row.start_date < today ? today : row.start_date;
    const week = weekOf(anchor);
    if (!week) continue;
    if (!groups.has(week)) groups.set(week, []);
    groups.get(week)!.push(row);
  }
  return Array.from(groups.entries())
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([week, weekRows]) => ({ week, rows: weekRows }));
}

export default function UpcomingCrewDaysOff() {
  const timeZone = useTenantTimeZone();
  const query = useQuery({
    queryKey: ['field-time-off-upcoming'],
    queryFn: () =>
      api.employee.upcomingTimeOff().then((r: any) => r.data?.data ?? r.data ?? []),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failure = queryLoadFailure(query);

  const heading = (
    <div className="flex items-center gap-1.5 text-sm font-semibold text-fieldiq-text">
      <CalendarDays size={14} className="text-fieldiq-accent" aria-hidden="true" />
      <h2>Upcoming crew days off</h2>
    </div>
  );

  let body: React.ReactNode;
  if (failure) {
    // Not "nobody's off": a hand reading the empty sentence over a failed
    // load would book on the strength of a request that never answered.
    body = (
      <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="crew days off" />
    );
  } else if (query.isLoading) {
    body = (
      <p className="text-xs text-fieldiq-muted" data-testid="upcoming-loading">
        Checking who's booked off…
      </p>
    );
  } else {
    const rows: TimeOffOverlap[] = Array.isArray(query.data) ? query.data : [];
    const groups = groupByWeek(rows, tenantCalendarDate(timeZone));
    body = groups.length === 0 ? (
      <p className="text-xs text-fieldiq-muted" data-testid="upcoming-empty">
        Nobody's booked off in the next 60 days.
      </p>
    ) : (
      // Scrolls inside itself: a 40-hand crew in a busy month is a long list,
      // and the request button above it must stay where the thumb expects it.
      <div className="max-h-80 overflow-y-auto pr-1" data-testid="upcoming-list">
        {groups.map(({ week, rows: weekRows }) => (
          <section key={week} aria-label={`Week of ${DAY(week)}`} className="py-1.5">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-fieldiq-muted">
              Week of {DAY(week)}
            </h3>
            <ul className="mt-1 space-y-0.5">
              {weekRows.map((row, i) => (
                <li
                  key={`${row.user_id ?? 'orphan'}-${row.start_date}-${row.end_date}-${i}`}
                  className="text-sm text-fieldiq-text"
                >
                  {overlapName(row)} · {DAY(row.start_date)} – {DAY(row.end_date)}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    );
  }

  return (
    <div
      data-testid="upcoming-crew-days-off"
      className="space-y-2 rounded-xl border border-fieldiq-border bg-fieldiq-card p-4"
    >
      {heading}
      {body}
    </div>
  );
}
